import { useCallback, useState, useRef } from 'react';

import {
  MIN_SELECTION_DURATION,
  MAX_SELECTION_DURATION,
  TIME_PRECISION,
} from '../utils/constants';
import {
  TrimAction,
  VideoSelection,
  constrainTrimLeft,
  constrainTrimRight,
  pixelsToTime,
  roundToPrecision,
} from '../utils/videoUtils';

interface UseTrimDragProps {
  selection: VideoSelection;
  videoDuration: number;
  stripWidth: number;
  onSelectionChange: (selection: VideoSelection) => void;
  onSeek?: (time: number) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  isPlaying?: boolean;
  onPlayPause?: () => void;
}

interface UseTrimDragReturn {
  trimAction: TrimAction;
  isDragging: boolean;
  leftTooltipVisible: boolean;
  rightTooltipVisible: boolean;
  handleLeftTrimStart: (screenX: number) => void;
  handleLeftTrimDrag: (screenX: number) => void;
  handleLeftTrimStop: () => void;
  handleRightTrimStart: (screenX: number) => void;
  handleRightTrimDrag: (screenX: number) => void;
  handleRightTrimStop: () => void;
}

/** Manages trim handle drag operations with constraints and video scrubbing. */
export function useTrimDrag({
  selection,
  videoDuration,
  stripWidth,
  onSelectionChange,
  onSeek,
  onDragStart,
  onDragEnd,
  isPlaying = false,
  onPlayPause,
}: UseTrimDragProps): UseTrimDragReturn {
  const [trimAction, setTrimAction] = useState<TrimAction>(null);
  const wasPlayingBeforeDragRef = useRef<boolean>(false);

  // Uses screenX for jitter-free dragging (absolute coords unaffected by element movement)
  const dragStartRef = useRef<{
    initialStartTime: number;
    initialEndTime: number;
    screenX: number;
  } | null>(null);

  const computeDragDelta = useCallback(
    (screenX: number): number => {
      if (!dragStartRef.current) return 0;
      const pixelDelta = screenX - dragStartRef.current.screenX;
      return pixelsToTime(pixelDelta, videoDuration, stripWidth);
    },
    [videoDuration, stripWidth]
  );

  const startDrag = useCallback(
    (
      action: TrimAction,
      initialStartTime: number,
      initialEndTime: number,
      screenX: number
    ) => {
      setTrimAction(action);
      wasPlayingBeforeDragRef.current = isPlaying;

      dragStartRef.current = {
        initialStartTime,
        initialEndTime,
        screenX,
      };

      if (isPlaying && onPlayPause) {
        onPlayPause();
      }

      onDragStart?.();
    },
    [isPlaying, onPlayPause, onDragStart]
  );

  const endDrag = useCallback(() => {
    setTrimAction(null);
    dragStartRef.current = null;

    if (wasPlayingBeforeDragRef.current && onPlayPause) {
      onPlayPause();
    }

    onDragEnd?.();
  }, [onPlayPause, onDragEnd]);

  const handleLeftTrimStart = useCallback(
    (screenX: number) => {
      startDrag('trim-left', selection.startTime, selection.endTime, screenX);
    },
    [startDrag, selection.startTime, selection.endTime]
  );

  const handleLeftTrimDrag = useCallback(
    (screenX: number) => {
      if (!dragStartRef.current) return;

      const timeDelta = computeDragDelta(screenX);
      const desiredStartTime =
        dragStartRef.current.initialStartTime + timeDelta;

      // Right handle stays anchored — only resize, never slide
      const newEndTime = dragStartRef.current.initialEndTime;
      const newStartTime = constrainTrimLeft(
        desiredStartTime,
        newEndTime,
        MIN_SELECTION_DURATION,
        MAX_SELECTION_DURATION
      );
      const newDuration = newEndTime - newStartTime;

      const roundedStartTime = roundToPrecision(newStartTime, TIME_PRECISION);
      const roundedEndTime = roundToPrecision(newEndTime, TIME_PRECISION);
      const roundedDuration = roundToPrecision(newDuration, TIME_PRECISION);

      if (
        roundedStartTime !== selection.startTime ||
        roundedEndTime !== selection.endTime
      ) {
        onSelectionChange({
          startTime: roundedStartTime,
          endTime: roundedEndTime,
          duration: roundedDuration,
        });

        onSeek?.(roundedStartTime);
      }
    },
    [
      selection.startTime,
      selection.endTime,
      computeDragDelta,
      onSelectionChange,
      onSeek,
    ]
  );

  const handleLeftTrimStop = useCallback(() => {
    endDrag();
  }, [endDrag]);

  const handleRightTrimStart = useCallback(
    (screenX: number) => {
      startDrag('trim-right', selection.startTime, selection.endTime, screenX);
    },
    [startDrag, selection.startTime, selection.endTime]
  );

  const handleRightTrimDrag = useCallback(
    (screenX: number) => {
      if (!dragStartRef.current) return;

      const timeDelta = computeDragDelta(screenX);
      const desiredEndTime = dragStartRef.current.initialEndTime + timeDelta;

      // Left handle stays anchored — only resize, never slide
      const newStartTime = dragStartRef.current.initialStartTime;
      const newEndTime = constrainTrimRight(
        desiredEndTime,
        newStartTime,
        videoDuration,
        MIN_SELECTION_DURATION,
        MAX_SELECTION_DURATION
      );
      const newDuration = newEndTime - newStartTime;

      const roundedStartTime = roundToPrecision(newStartTime, TIME_PRECISION);
      const roundedEndTime = roundToPrecision(newEndTime, TIME_PRECISION);
      const roundedDuration = roundToPrecision(newDuration, TIME_PRECISION);

      if (
        roundedStartTime !== selection.startTime ||
        roundedEndTime !== selection.endTime
      ) {
        onSelectionChange({
          startTime: roundedStartTime,
          endTime: roundedEndTime,
          duration: roundedDuration,
        });

        onSeek?.(roundedEndTime);
      }
    },
    [
      selection.startTime,
      selection.endTime,
      videoDuration,
      computeDragDelta,
      onSelectionChange,
      onSeek,
    ]
  );

  const handleRightTrimStop = useCallback(() => {
    endDrag();
  }, [endDrag]);

  return {
    trimAction,
    isDragging: trimAction !== null,
    leftTooltipVisible: trimAction === 'trim-left',
    rightTooltipVisible: trimAction === 'trim-right',
    handleLeftTrimStart,
    handleLeftTrimDrag,
    handleLeftTrimStop,
    handleRightTrimStart,
    handleRightTrimDrag,
    handleRightTrimStop,
  };
}
